import { useEffect, useRef } from "react";
import { trackCTA } from "@/lib/tracking";

/**
 * Fires a `cta_impression` event exactly once when the target element
 * becomes at least `threshold` visible in the viewport. Use per CTA
 * surface (hero / mid-page / final) to measure section-level conversion.
 */
export function useCTAImpression<T extends HTMLElement = HTMLDivElement>(
  surface: string,
  extra: Record<string, string | number | boolean | undefined> = {},
  threshold = 0.5,
) {
  const ref = useRef<T | null>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || firedRef.current || typeof IntersectionObserver === "undefined") return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !firedRef.current) {
            firedRef.current = true;
            trackCTA("cta_impression", { surface, ...extra });
            io.disconnect();
            break;
          }
        }
      },
      { threshold },
    );

    io.observe(el);
    return () => io.disconnect();
  }, [surface, threshold, extra]);

  return ref;
}
